import { useState } from "react";
import clsx from "clsx";
import {
  BriefcaseBusinessIcon,
  ChevronRight,
  ChevronsUpDownIcon,
  CircleHelpIcon,
  FactoryIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MoreHorizontalIcon,
  PanelLeftClose,
  PanelLeftOpen,
  SettingsIcon,
  UsersIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router";

import { ContactUsDialog } from "@/components";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCurrentCompanyId } from "@/hooks";
import { useSignOut } from "@/hooks/mutations";
import { routes } from "@/lib/routes";
import { storage } from "@/lib/storage";
import { jobsStore } from "@/stores";
import { JobStatus, SignInResponse } from "@/types";

import laborupBlueGear from "@/assets/laborup_blue_gear.svg";
import laborupDarkText from "@/assets/laborup_dark_text.svg";

const numberOfJobsToDisplayInPipeline = 10;

const UserWithCompanyAndAvatar = ({ user }: { user: SignInResponse }) => {
  const currentCompanyId = useCurrentCompanyId();

  // Match the current company id in the url to a company that is part of the user's companies.
  // TODO: Perhaps in the future we will store the whole current company object in a store.
  const currentCompany = user.companies.find(
    (company) => company.id === currentCompanyId,
  );
  if (!currentCompany) {
    throw new Error("Current company not found in user's companies");
  }

  return (
    <>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-200">
        <p className="text-[10px] font-medium text-slate-900">
          {user.firstName[0]}
          {user.lastName[0]}
        </p>
      </div>
      <div className="flex flex-col gap-[2px]">
        <p className="font-semibold leading-none">{currentCompany.name}</p>
        <p className="text-xs leading-none">{user.email}</p>
      </div>
    </>
  );
};

export const AppSidebar = () => {
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [footerDropdownOpen, setFooterDropdownOpen] = useState(false);
  const [organizationCollapsibleOpen, setOrganizationCollapsibleOpen] =
    useState(true);
  const [pipelineCollapsibleOpen, setPipelineCollapsibleOpen] = useState(true);

  const currentCompanyId = useCurrentCompanyId();
  const navigate = useNavigate();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const { mutate: signOut } = useSignOut();
  const isLoading = jobsStore((state) => state.isLoading);
  const jobs = jobsStore((state) => state.jobs);
  const activeJobs = jobs.filter(({ job }) => job.status === JobStatus.ACTIVE);
  // TODO: This is a common pattern so we should create a reusable piece of logic.
  const user = storage.getUser();
  if (!user) {
    throw new Error("User not found in local storage");
  }

  const handleSignOut = () => {
    signOut();
  };

  return (
    <>
      <Sidebar collapsible="icon">
        {/* Header */}
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                // TODO: This is a hack to ensure that the Laborup blue gear icon
                // is centered. There is probably a better way to do it but it's
                // also referenced here:
                // https://ui.shadcn.com/docs/components/sidebar#styling
                className="group-data-[collapsible=icon]:!p-1"
              >
                <Link to="">
                  <img
                    alt="Laborup blue gear"
                    className="h-6 w-6"
                    src={laborupBlueGear}
                  />
                  <img
                    alt="Laborup dark text"
                    className="h-[18px] w-[76px]"
                    src={laborupDarkText}
                  />
                </Link>
              </SidebarMenuButton>
              <SidebarMenuAction
                onClick={() => {
                  setSidebarOpen(false);
                }}
              >
                <PanelLeftClose />
              </SidebarMenuAction>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Content */}
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {!sidebarOpen && ( // TODO: See if there is a better way to do this.
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => {
                        setSidebarOpen(true);
                      }}
                      tooltip="Open"
                    >
                      <PanelLeftOpen />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={routes.isDashboardRouteActive(location.pathname)}
                    tooltip="Dashboard"
                    asChild
                  >
                    <Link to="">
                      <LayoutDashboardIcon />
                      <span className="text-base font-medium">Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={routes.isJobsRouteActive(location.pathname)}
                    tooltip="Jobs"
                    asChild
                  >
                    <Link to="jobs">
                      <BriefcaseBusinessIcon />
                      <span className="text-base font-medium">Jobs</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarSeparator />
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <Collapsible
                  className="group/collapsible"
                  onOpenChange={(open) => {
                    if (!sidebarOpen) {
                      // TODO: See if there is a better way to do this.
                      // If the sidebar is in the process of opening, ensure
                      // that the pipeline collapsible will be open.
                      setPipelineCollapsibleOpen(true);
                    } else {
                      setPipelineCollapsibleOpen(open);
                    }
                  }}
                  open={pipelineCollapsibleOpen}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        onClick={() => {
                          // If the sidebar is closed when the pipeline button
                          // is clicked, open the sidebar.
                          if (!sidebarOpen) {
                            setSidebarOpen(true);
                          }
                        }}
                        isActive={routes.isJobsPipelineRouteActive(
                          location.pathname,
                        )}
                        tooltip="Pipeline"
                      >
                        <UsersIcon />
                        <span className="text-base font-medium">Pipeline</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {!isLoading && (
                          <>
                            {activeJobs.length === 0 ? (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton className="pointer-events-none text-slate-500">
                                  No jobs found
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ) : (
                              <>
                                {activeJobs
                                  .slice(0, numberOfJobsToDisplayInPipeline)
                                  .map(({ job }) => (
                                    <TooltipProvider
                                      delayDuration={0}
                                      key={job.id}
                                    >
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <SidebarMenuSubItem key={job.id}>
                                            <SidebarMenuSubButton
                                              isActive={routes.isSpecificJobPipelineRouteActive(
                                                location.pathname,
                                                job.id,
                                              )}
                                              asChild
                                            >
                                              <Link
                                                to={`jobs/${job.id}/pipeline`}
                                              >
                                                <p className="truncate">
                                                  {job.title}
                                                </p>
                                              </Link>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                          <p>{job.title}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ))}
                                {activeJobs.length >
                                  numberOfJobsToDisplayInPipeline && (
                                  <SidebarMenuSubItem>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <SidebarMenuSubButton
                                          className="flex items-center justify-between"
                                          isActive={routes.isAnyJobInListPipelineRouteActive(
                                            location.pathname,
                                            activeJobs
                                              .slice(
                                                numberOfJobsToDisplayInPipeline,
                                              )
                                              .map(({ job }) => job.id),
                                          )}
                                        >
                                          View more <MoreHorizontalIcon />
                                        </SidebarMenuSubButton>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent
                                        side="right"
                                        align="start"
                                      >
                                        {activeJobs
                                          .slice(
                                            numberOfJobsToDisplayInPipeline,
                                          )
                                          .map(({ job }) => (
                                            <DropdownMenuItem
                                              className={clsx(
                                                routes.isSpecificJobPipelineRouteActive(
                                                  location.pathname,
                                                  job.id,
                                                ) && "bg-slate-100", // The default hover state for the dropdown menu item.
                                              )}
                                              key={job.id}
                                              onClick={() => {
                                                void navigate(
                                                  `/companies/${currentCompanyId}/jobs/${job.id}/pipeline`,
                                                );
                                              }}
                                            >
                                              {job.title}
                                            </DropdownMenuItem>
                                          ))}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </SidebarMenuSubItem>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarSeparator />
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <Collapsible
                  className="group/collapsible"
                  onOpenChange={(open) => {
                    if (!sidebarOpen) {
                      // TODO: See if there is a better way to do this.
                      // If the sidebar is in the process of opening, ensure
                      // that the organization collapsible will be open.
                      setOrganizationCollapsibleOpen(true);
                    } else {
                      setOrganizationCollapsibleOpen(open);
                    }
                  }}
                  open={organizationCollapsibleOpen}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        onClick={() => {
                          // If the sidebar is closed when the organization button
                          // is clicked, open the sidebar.
                          if (!sidebarOpen) {
                            setSidebarOpen(true);
                          }
                        }}
                        isActive={routes.isCompanyProfileRouteActive(
                          location.pathname,
                        )}
                        tooltip="Organization"
                      >
                        <FactoryIcon />
                        <span className="text-base font-medium">
                          Organization
                        </span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            isActive={routes.isCompanyProfileRouteActive(
                              location.pathname,
                            )}
                            asChild
                          >
                            <Link to="profile">
                              <p className="truncate">Company Profile</p>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu
                onOpenChange={setFooterDropdownOpen}
                open={footerDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" // TODO: Figure out if necessary.
                    size="lg"
                  >
                    <UserWithCompanyAndAvatar user={user} />
                    <ChevronsUpDownIcon className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={sidebarOpen ? "top" : "right"}
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" // TODO: Figure out if `min-w-56` is necessary and what `--radix-dropdown-menu-trigger-width` is referring to...
                >
                  <DropdownMenuItem asChild>
                    <Link to="settings">
                      <SettingsIcon className="h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setContactDialogOpen(true);
                    }}
                  >
                    <CircleHelpIcon className="h-4 w-4" />
                    Contact us
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOutIcon />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <ContactUsDialog
        onOpenChange={setContactDialogOpen}
        // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2197220521
        open={!footerDropdownOpen && contactDialogOpen}
      />
    </>
  );
};
